import type { ReactElement } from 'react';
import { createContext, useContext } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InjectComponentValue<P = any> = Record<
  string,
  Record<
    string,
    | undefined
    | ReactElement
    | ((params: Required<InjectComponentProps<P>>['params']) => ReactElement | undefined)
  >
>;

export const InjectComponentContext = createContext<InjectComponentValue>({});

/**
 * Use to provide context of injected components by id.
 */
export const InjectComponentProvider = InjectComponentContext.Provider;

const useInjectComponent = <P,>(
  namespace: InjectComponentProps<P>['namespace'],
  name: InjectComponentProps<P>['name'],
  params: InjectComponentProps<P>['params']
) => {
  const context = useContext(InjectComponentContext);
  let component: ReactElement | undefined;
  const inject = context[namespace]?.[name];
  if (typeof inject === 'function') {
    if (params) component = inject(params);
  } else if (inject) component = inject;
  return component;
};

export interface InjectComponentProps<P> {
  /**
   * Namespace in which to inject component.
   * eg. ComponentName
   */
  readonly namespace: string;

  /**
   * Name of the area that will inject component.
   * eg. title
   */
  readonly name: string;

  /**
   * Default render when no inject component is provided.
   */
  readonly children?: ReactElement;

  /**
   * Pass additional parameters from inside the component that has injectable areas.
   */
  readonly params?: P;
}

/**
 * Inject a component if it's specified in context by id or use the default one.
 * Also can inject components before/after the default one.
 *
 * !!! Affects CSR bundle size !!!
 * Should take into consideration that the default components will be brought in
 * even when they are replaced by something else. (unless hydration is avoided)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function InjectComponent<P = any>({
  namespace,
  name,
  params,
  children,
}: InjectComponentProps<P>): ReactElement {
  const inject = useInjectComponent(namespace, name, params);
  const injectBefore = useInjectComponent(namespace, `${name}_before`, params);
  const injectAfter = useInjectComponent(namespace, `${name}_after`, params);

  const component = (
    <>
      {injectBefore}
      {inject ?? children}
      {injectAfter}
    </>
  );

  return component;
}
InjectComponent.displayName = 'InjectComponent';
